import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEdmDocumentationBulkDestroyMutation } from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import { APP_URLS } from 'constants/urls';

interface UseDocumentViewActionsProps {
  isViewLocked?: boolean;
  isAiDetectionEnabled?: boolean;
}

export default function useDocumentViewActions({
  isViewLocked,
  isAiDetectionEnabled,
}: UseDocumentViewActionsProps = {}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const documentQueryHookResult = useEdmDocumentationQueryHook({
    id,
    isViewLocked,
    isAiDetectionEnabled,
  });

  const { data, loading, error, firstLoading } = documentQueryHookResult;

  const [destroyMutation] = useEdmDocumentationBulkDestroyMutation();

  const breadCrumbsData = getBreadCrumbsDataByKey({
    pathname,
    t,
    entity: data || { id },
  });

  const onEdit = () => {
    navigate(
      APP_URLS.dashboard.documentation.edit.getDynamicPath({
        pathParts: { id },
      }),
    );
  };

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const onConfirmedDelete = () => {
    closeDeleteModal();
    destroyMutation({
      variables: { ids: [id] },
      onCompleted: () => {
        toast({
          content: t('delete-document-success'),
        });
        navigate(APP_URLS.dashboard.documentation.index.getPathWithQuery());
      },
    });
  };

  return {
    id,
    documentQueryHookResult,
    breadCrumbsData,
    loading,
    error,
    firstLoading,
    isDeleteModalOpen,
    onEdit,
    openDeleteModal,
    closeDeleteModal,
    onConfirmedDelete,
  };
}
